import { Injectable, inject, signal } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, Subject, finalize } from 'rxjs';
import { Notification, NotificationResponse } from '../models/notification';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private apiService = inject(ApiService);
  
  // Single source of truth for notifications
  notifications = signal<Notification[]>([]);
  isLoading = signal(false);

  // Subject to notify components when notifications need to be refreshed
  refreshNotifications$ = new Subject<void>();

  /**
   * Fetch all notifications for the admin
   * @returns Observable of NotificationResponse
   */
  getNotifications(): Observable<NotificationResponse> {
    return this.apiService.get<NotificationResponse>('admin/notifications');
  }

  /**
   * Mark a specific notification as read by its ID
   * @param id - notification ID
   * @returns Observable of the update result
   */
  markAsRead(id: string): Observable<any> {
    return this.apiService.patch(`admin/notifications/${id}/read`, {});
  }

  /**
   * Bulk mark all unread notifications as read
   * @returns Observable of the result
   */
  markAllAsRead(): Observable<any> {
    return this.apiService.patch('admin/notifications/read-all', {});
  }

  // Method to refresh notifications and update the signal
  refresh(): void {
    if (this.isLoading()) return;
    this.isLoading.set(true);
    this.apiService.get<NotificationResponse>('admin/notifications')
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response: any) => {
          const data = response?.data || response;
          const docs = data?.docs || (Array.isArray(data) ? data : []);
          const normalizedDocs = Array.isArray(docs)
            ? docs.map((notification: Notification) => this.normalizeNotificationContent(notification))
            : [];
          this.notifications.set(normalizedDocs);
        },
        error: (error) => {
          console.error('Error refreshing notifications:', error);
          this.notifications.set([]);
        }
      });
  }

  private normalizeNotificationContent(notification: Notification): Notification {
    if (![
      'kyc_review', 'admin:kyc-review-completed', 'kyc_completed',
      'kyb_review', 'admin:kyb-review-completed', 'kyb_completed',
      'kyc_rfi', 'kyb_rfi', 'verification_completed', 'verification_rejected',
      'kyb_verified', 'kyc_verified', 'kyc_rejected', 'kyb_rejected'
    ].includes(notification.type)) {
      return this.fixCommonTypos(notification);
    }

    const verificationType = this.resolveVerificationType(notification);
    if (!verificationType) {
      return this.fixCommonTypos(notification);
    }

    const title = this.replaceVerificationLabel(notification.title, verificationType);
    const message = this.replaceVerificationLabel(notification.message, verificationType);

    return this.fixCommonTypos({
      ...notification,
      title,
      message
    });
  }

  private fixCommonTypos(notification: Notification): Notification {
    return {
      ...notification,
      title: this.correctSpelling(notification.title),
      message: this.correctSpelling(notification.message)
    };
  }

  private correctSpelling(text: string): string {
    if (!text) return text;
    return text.replace(/\berjected\b/gi, 'rejected');
  }

  private replaceVerificationLabel(text: string, verificationType: 'KYC' | 'KYB'): string {
    if (!text) return text;
    return text
      .replace(/\bkyc\b/gi, verificationType)
      .replace(/\bkyb\b/gi, verificationType);
  }

  private resolveVerificationType(notification: Notification): 'KYC' | 'KYB' | null {
    const meta = notification.meta_data || {};
    const rawType = [
      meta['customer_type'],
      meta['account_type'],
      meta['client_type'],
      meta['user_type'],
      meta['profile_type']
    ].find((value) => typeof value === 'string' && value.trim().length > 0) as string | undefined;

    const normalizedType = (rawType || '').trim().toLowerCase();
    if (['business', 'company', 'corporate', 'organization', 'organisation', 'kyb'].includes(normalizedType)) {
      return 'KYB';
    }
    if (['individual', 'personal', 'retail', 'kyc'].includes(normalizedType)) {
      return 'KYC';
    }

    const combinedText = `${notification.title || ''} ${notification.message || ''}`.toLowerCase();
    if (/\bkyb\b|business verification/i.test(combinedText)) return 'KYB';
    if (/\bkyc\b|personal verification/i.test(combinedText)) return 'KYC';

    return null;
  }
}
